import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import { getAuthenticatedSession, getCurrentUser, getDefaultExperience } from 'src/api/authentication';
import { identifyActor } from '@repo/shared/analytics';
import { AuthenticatedUserProvider } from 'src/contexts/AuthenticatedUserContext';
import { ConfigProvider } from 'src/contexts/ConfigContext';
import { UserPreferenceProvider } from 'src/contexts/UserPreferenceContext';
import LayoutWrapper from 'src/layouts/admin';
import { ThemeProvider } from '@mui/material/styles';
import { muiExitTheme } from '@repo/theme';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthenticatedUser, PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getSelectedSite, getSites } from 'src/api/admin/sites';
import { getUserPreferences } from 'src/api/userPreferences';
import { NotFound } from '@repo/components';
import { hardRedirect } from 'src/utils/hardRedirect';

// Config and system pages are not yet part of the 2.0 experience, so they stay in this app.
const isConfigOrSystemPath = (pathname: string) => pathname.startsWith('/config') || pathname.startsWith('/system');

export const Route = createFileRoute('/_admin')({
	staleTime: 1000 * 60 * 5,
	loader: async ({ location }) => {
		const authenticatedSession = await getAuthenticatedSession();
		if (!authenticatedSession.user) {
			throw redirect({
				to: '/authentication/login',
				search: {
					redirect: location.href,
				},
			});
		}

		const verifyPermission = getVerifyPermission(authenticatedSession.user.permissions || []);
		if (!verifyPermission(PERMISSIONS.adminView) && !verifyPermission(PERMISSIONS.siteAdmin)) {
			throw redirect({ to: '/' });
		}

		// When the 2.0 experience is the default, send users to the new admin app.
		// On a direct/hard load of a non-config/system page we redirect here to avoid a flash;
		// client-side navigations away from config/system are handled by the effect below.
		const experience = await getDefaultExperience();
		if (experience === '2.0' && !isConfigOrSystemPath(location.pathname)) {
			await hardRedirect('/app/admin/');
		}

		const [userPreferences, selectedSiteRaw, sitesRes, currentUser] = await Promise.all([
			getUserPreferences(),
			getSelectedSite().catch(() => null),
			getSites(),
			getCurrentUser(),
		]);

		const sites = sitesRes?.data ?? [];
		const selectedSite = selectedSiteRaw ?? sites[0] ?? null;

		const isSSOUser = authenticatedSession.ssoAvailable && !!currentUser.externalId;
		const hasTwoFactor = !!currentUser.hasTwoFactor;

		return { authenticatedSession, userPreferences, selectedSite, sites, isSSOUser, hasTwoFactor, experience };
	},
	component: AuthenticatedLayout,
	notFoundComponent: () => <NotFound />,
});

function AuthenticatedLayout() {
	const { authenticatedSession, userPreferences, selectedSite, sites, isSSOUser, hasTwoFactor, experience } =
		Route.useLoaderData();
	const { pathname } = useLocation();

	useEffect(() => {
		identifyActor(authenticatedSession?.user);
	}, [authenticatedSession?.user?.id]);

	// Loader redirects on hard load; this handles client-side navigation from config/system
	// (whose loader result is cached, so the loader-level redirect above does not re-run).
	useEffect(() => {
		if (experience === '2.0' && !isConfigOrSystemPath(pathname)) {
			window.location.href = '/app/admin/';
		}
	}, [experience, pathname]);

	return (
		<ThemeProvider theme={muiExitTheme}>
			<CssBaseline />
			<AuthenticatedUserProvider authenticatedSession={authenticatedSession}>
				<ConfigProvider>
					<UserPreferenceProvider>
						<LayoutWrapper
							authenticatedUser={(authenticatedSession?.user as AuthenticatedUser) || null}
							userPreferences={userPreferences}
							selectedSite={selectedSite}
							isSSOUser={isSSOUser}
							hasTwoFactor={hasTwoFactor}
							sites={sites}
						>
							<Outlet />
						</LayoutWrapper>
					</UserPreferenceProvider>
				</ConfigProvider>
			</AuthenticatedUserProvider>
		</ThemeProvider>
	);
}
